import { FC, useMemo } from 'react';
import {
  ValidationErrorType,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  Detail,
  getValidationErrorLevelColor,
  SystemClassifierItemDto,
  SystemClassifierCode,
} from '@atlas-frontend/sdk';
import { Badge, Button, Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ValidationErrorDetailsModal.translations.json';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';
import { formatErrorDescription } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'validation-error-details-modal',
);
addTranslations(translations);

type Props = {
  error: ValidationErrorType;
  systemClassifierItemsById: {
    [K in SystemClassifierCode[][number]]: Map<
      SystemClassifierItemDto[keyof SystemClassifierItemDto],
      SystemClassifierItemDto
    >;
  };
  onClose: () => void;
};

export const ValidationErrorDetailsModal: FC<Props> = ({
  error,
  systemClassifierItemsById,
  onClose,
}) => {
  const { t } = useTranslation();

  const errorLevel = useMemo(() => {
    if (!error) return null;

    return systemClassifierItemsById.PROC_VAL_ERROR_LEVEL.get(
      error.errorLevelId,
    );
  }, [error, systemClassifierItemsById]);

  const expressionOperator = useMemo(() => {
    if (!error?.expressionOperatorId) return null;

    return systemClassifierItemsById.VALIDATION_RULE_OPERATOR.get(
      error.expressionOperatorId,
    );
  }, [error, systemClassifierItemsById]);

  return (
    <Modal
      maskClosable={false}
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <Button id="close-modal" type="primary" onClick={onClose}>
              {t('button.close')}
            </Button>
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="50rem"
    >
      <ModalHeader>
        <ModalTitle>{t('title')}</ModalTitle>
      </ModalHeader>
      {error && (
        <Padding scrollable>
          <Flex vertical gap="small">
            <h5>{t('mainData')}</h5>
            <Detail label={t('dataObjectDescription')}>
              {error.processingDataObjectName}
            </Detail>
            <Detail label={t('ruleCode')}>
              {error.ruleCode && error.validationRuleId ? (
                <UiLink
                  id={`validation-rule-link-${error.validationRuleId}`}
                  path={path.outputDataObjectView}
                  params={{
                    id: error.processingDataObjectDescriptionId,
                    segment: 'validationRules',
                    validationRuleId: error.validationRuleId,
                  }}
                >
                  {error.ruleCode}
                </UiLink>
              ) : (
                error.ruleCode
              )}
            </Detail>
            {errorLevel && (
              <Detail label={t('errorLevel')}>
                {
                  <>
                    <Badge
                      color={getValidationErrorLevelColor(errorLevel.code)}
                    />{' '}
                    {errorLevel.name}
                  </>
                }
              </Detail>
            )}
            <Detail label={t('ignoreError')}>
              {error.ignoreError !== null
                ? t(error.ignoreError ? 'yes' : 'no')
                : null}
            </Detail>
            {error.ignoreError && (
              <Detail label={t('ignoreReason')}>{error.ignoreReason}</Detail>
            )}
          </Flex>
          <Padding top>
            <Flex vertical gap="small">
              <h5>{t('description')}</h5>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {formatErrorDescription(error.description)}
              </div>
              <Detail label={t('condition')}>{error.condition}</Detail>
              <Detail label={t('expressionLeftHandSide')}>
                {error.expressionLeftHandSide}
              </Detail>
              <Detail label={t('expressionOperator')}>
                {expressionOperator?.name}
              </Detail>
              <Detail label={t('expressionRightHandSide')}>
                {error.expressionRightHandSide}
              </Detail>
              <Detail label={t('tolerance')}>{error.tolerance}</Detail>
            </Flex>
          </Padding>
        </Padding>
      )}
    </Modal>
  );
};
